import { useSearchParams } from 'react-router-dom';
import {
  ActionLink,
  DimensionProgress,
  LearningShell,
  ToneBadge,
} from '../features/learning-space/components';
import { buildLearningRouteScope } from '@examflow/learning-space';
import {
  buildAbilityDiagnosisPageModel,
  resolveAbilityDiagnosisPageStatus,
  type PageAction,
} from '@examflow/learning-space';
import { resolveReadyDiagnosisSectionIds } from '@examflow/learning-space';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { useAbilitySnapshots } from '../features/learning-space/useAbilitySnapshots';
import { useRecentMockExamRecords } from '../features/learning-space/useRecentMockExamRecords';
import {
  buildLearningEvidenceRecordQueryScope,
  useRecentTrainingAttempts,
} from '../features/learning-space/useRecentTrainingAttempts';
import { useAccessibleBanks } from '../hooks/useApiQueries';
import {
  formatExamSpaceIdentityLabel,
  resolveExamSpaceTheme,
} from '@examflow/learning-space';
import { buildPlatformScope } from '@examflow/core';

const ABILITY_SNAPSHOT_SOURCE_LABELS: Record<string, string> = {
  diagnosis: '诊断',
  training: '训练',
  mock: '模考',
};

function formatAbilitySnapshotDate(createdAt: string | number): string {
  const time = typeof createdAt === 'number' ? createdAt : Date.parse(createdAt);
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return String(createdAt);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

export default function ExamAbilityDiagnosisPage() {
  const [searchParams] = useSearchParams();
  const viewer = useLearningViewer(buildLearningRouteScope(searchParams));
  const evidenceScope = buildLearningEvidenceRecordQueryScope(viewer);
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const accessibleBanks = useAccessibleBanks(activeScope ?? undefined);
  const trainingAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...evidenceScope,
    limit: 30,
  });
  const abilitySnapshots = useAbilitySnapshots({
    userId: viewer.userId,
    examType: evidenceScope.examType ?? viewer.activeExamSpace.examCode,
    limit: 20,
  });
  const mockExamRecords = useRecentMockExamRecords({
    userId: viewer.userId,
    examType: viewer.activeExamSpace.examCode,
    languageId: evidenceScope.languageId ?? null,
    limit: 3,
  });
  const status =
    trainingAttempts.status === 'unavailable' ||
    abilitySnapshots.status === 'unavailable' ||
    mockExamRecords.status === 'unavailable'
      ? 'updateError'
      : resolveAbilityDiagnosisPageStatus({
          records: trainingAttempts.records,
          mockExamRecords: mockExamRecords.records,
          abilitySnapshots: abilitySnapshots.snapshots,
          examCode: viewer.activeExamSpace.examCode,
        });
  const readyDiagnosisSectionIds =
    accessibleBanks.data && !accessibleBanks.isLoading
      ? resolveReadyDiagnosisSectionIds(viewer.activeExamSpace.examCode, accessibleBanks.data)
      : null;
  const model = buildAbilityDiagnosisPageModel(
    viewer,
    status,
    trainingAttempts.records,
    mockExamRecords.records,
    abilitySnapshots.snapshots,
    { readyDiagnosisSectionIds }
  );
  const theme = resolveExamSpaceTheme(viewer.activeExamSpace.examCode);
  const identityLabel = formatExamSpaceIdentityLabel(
    viewer.activeExamSpace.examCode,
    viewer.activeExamSpace.examName
  );
  const noDiagnosis = model.status === 'noDiagnosis';
  const hasSnapshotReport = model.trend.length > 0;
  const showLevelHero = !noDiagnosis || hasSnapshotReport;
  const showReportBody = !noDiagnosis || hasSnapshotReport;
  const diagnosisBlocked = Boolean(model.nextStep.action.disabled);
  const emptyBaseline = noDiagnosis && !hasSnapshotReport;
  const trainingFallbackAction: PageAction = {
    ...model.prescription.primaryAction,
    id: 'start-training-before-diagnosis',
    label: `先练${model.prescription.moduleLabel}`,
    kind: 'primary',
  };
  const primaryAction: PageAction =
    noDiagnosis && diagnosisBlocked ? trainingFallbackAction : model.nextStep.action;
  const secondaryAction: PageAction =
    noDiagnosis && diagnosisBlocked
      ? { ...model.secondaryAction, kind: 'ghost' }
      : noDiagnosis
        ? {
            ...model.prescription.primaryAction,
            id: 'skip-to-training',
            label: '先去专项训练',
            kind: 'ghost',
          }
        : { ...model.secondaryAction, kind: 'ghost' };

  return (
    <LearningShell viewer={viewer} activeNav="diagnosis" statusLabel={model.snapshot.stabilityLabel}>
      <section data-testid="exam-space-diagnosis-hero">
        <p className="text-xs font-semibold tracking-[0.14em]" style={{ color: theme.accent }}>
          {identityLabel}
        </p>
        <h1
          className="mt-3 max-w-3xl font-black tracking-tight text-[#111827]"
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.08 }}
        >
          {emptyBaseline ? '建立能力基线' : '能力诊断'}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
          {emptyBaseline
            ? diagnosisBlocked
              ? model.nextStep.action.disabledReason ??
                '这门考试暂时还没有可练的题目。先去专项训练，练过的记录也会更新这里的判断。'
              : '用一轮跨科目抽样，先摸清强弱，再决定练什么。也可以跳过，直接去专项。'
            : model.nextStep.description}
        </p>

        {showLevelHero && (
          <div className="mt-8 flex flex-wrap items-end gap-8">
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500">能力结论</div>
              <div
                className="mt-2 font-black tracking-tight"
                style={{
                  color: theme.accent,
                  fontSize: 'clamp(3rem, 8vw, 4.5rem)',
                  lineHeight: 1,
                }}
              >
                {model.snapshot.displayLevel}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <ToneBadge
                  tone={
                    model.status === 'updateError'
                      ? 'danger'
                      : model.status === 'lowData'
                        ? 'warning'
                        : 'success'
                  }
                >
                  {model.snapshot.stabilityLabel}
                </ToneBadge>
                <span className="text-sm text-slate-500">{model.abilityReport.scopeLabel}</span>
              </div>
            </div>
            <div className="max-w-md pb-1 text-sm leading-6 text-slate-600">
              {model.snapshot.conclusion}
            </div>
          </div>
        )}

        {emptyBaseline && (
          <p className="mt-6 text-sm text-slate-500">
            当前还没有等级结论
            <span className="mx-1.5 text-slate-300">·</span>
            {diagnosisBlocked ? '诊断入口暂不可用' : '完成一次诊断后显示'}
          </p>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <ActionLink action={primaryAction} />
          <ActionLink action={secondaryAction} />
        </div>
        {emptyBaseline && diagnosisBlocked && model.nextStep.action.disabledReason ? (
          <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-500">
            {model.nextStep.action.disabledReason}
          </p>
        ) : (
          <p className="mt-5 text-sm font-semibold text-[#111827]">{model.nextStep.title}</p>
        )}
      </section>

      {showReportBody && (
        <section className="mt-12 space-y-8" data-testid="exam-space-diagnosis-body">
          <div>
            <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">
              推荐训练
            </h2>
            <div className="rounded-md border border-white/70 bg-white/55 px-5 py-4">
              <ToneBadge tone="success">{model.prescription.moduleLabel}</ToneBadge>
              <h3 className="mt-3 text-xl font-bold text-[#111827]">{model.prescription.title}</h3>
              <p className="mt-2 text-sm text-slate-600">
                {model.prescription.durationMinutes} 分钟 · {model.prescription.questionCount}{' '}
                题。练完后再看诊断是否更稳。
              </p>
              <p className="mt-2 text-xs font-semibold text-slate-500">
                训练范围：{model.prescription.prescriptionLabel}
              </p>
              <p className="mt-3 text-xs text-slate-500">
                {model.updatePolicy.nextUpdateLabel}
                <span className="mx-1.5">·</span>
                {model.updatePolicy.requiredEvidenceLabel}
              </p>
            </div>
          </div>

          {model.mockExamEvidence.recordCount > 0 && (
            <div>
              <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">
                模考证据
              </h2>
              <div className="rounded-md border border-white/70 bg-white/55 px-5 py-4 text-sm">
                <div className="font-semibold text-[#111827]">
                  {model.mockExamEvidence.scopeLabel}
                </div>
                <div className="mt-2 text-slate-600">{model.mockExamEvidence.latestResultLabel}</div>
                <div className="mt-1 text-slate-500">{model.mockExamEvidence.scoreLabel}</div>
              </div>
            </div>
          )}

          {model.writingEvidence && (
            <div data-testid="exam-space-writing-evidence">
              <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">
                写作证据
              </h2>
              <div className="rounded-md border border-white/70 bg-white/55 px-5 py-4">
                <p className="text-sm leading-6 text-slate-600">
                  {model.writingEvidence.reportLine ?? model.writingEvidence.focusReason}
                </p>
                <div className="mt-4">
                  <ActionLink
                    action={{
                      id: 'start-writing-from-evidence',
                      label: model.writingEvidence.actionLabel,
                      kind: 'secondary',
                      href: model.writingEvidence.entryHref,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-4 text-[11px] font-bold tracking-[0.18em] text-slate-500">
              能力维度
            </h2>
            <div className="space-y-5 rounded-md border border-white/70 bg-white/55 px-5 py-5">
              {model.dimensions.map((dimension) => (
                <DimensionProgress key={dimension.label} {...dimension} />
              ))}
            </div>
          </div>

          {model.trend.length > 0 && (
            <div>
              <h2 className="mb-4 text-[11px] font-bold tracking-[0.18em] text-slate-500">
                能力趋势
              </h2>
              <div className="space-y-4 rounded-md border border-white/70 bg-white/55 px-5 py-5">
                {model.trend.map((point) => (
                  <div
                    key={`${point.createdAt}-${point.source}`}
                    className="grid grid-cols-[120px_minmax(0,1fr)_56px] items-center gap-4 text-sm"
                  >
                    <div className="text-slate-500">
                      {formatAbilitySnapshotDate(point.createdAt)} ·{' '}
                      {ABILITY_SNAPSHOT_SOURCE_LABELS[point.source] ?? point.source}
                      {point.level ? ` · ${point.level}` : ''}
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${Math.min(100, Math.max(0, point.overallScore))}%`,
                          backgroundColor: theme.accent,
                        }}
                      />
                    </div>
                    <div className="text-right font-semibold text-[#111827]">
                      {point.overallScore}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </LearningShell>
  );
}
